import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PhoneCall, CheckCircle2, Star, Shield, Award, Clock, Sparkles, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import { mockData } from '../data/mock';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageMeta from '../hooks/usePageMeta';

const GarageEpoxy = () => {
  usePageMeta('Garage Epoxy Flooring Sydney | Alpha Epoxy Floors | 35% Off', 'Transform your garage with premium epoxy flooring in Sydney. Oil-resistant, anti-slip, stunning finishes. 15+ years experience. Free quotes. 35% off. Call 0451 945 455.');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.email) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }
    toast({
      title: "Quote Request Received!",
      description: "We'll contact you within 24 hours about your garage project."
    });
    setFormData({ name: '', phone: '', email: '', notes: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-black">
      <Header activePage="garage-epoxy" />

      <div className="pt-28">
        {/* Hero Section with Form */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-20" style={{ backgroundColor: '#1C2541' }}>
          <div className="absolute inset-0">
            <img 
              src="/images/hero-bg.png" 
              alt="Garage Epoxy Background"
              className="w-full h-full object-cover opacity-60"
              style={{ objectPosition: '30% center' }}
              loading="eager"
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(28, 37, 65, 0.2) 0%, rgba(28, 37, 65, 0.4) 30%, rgba(28, 37, 65, 0.6) 60%, rgba(28, 37, 65, 0.7) 100%)' }}></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full animate-fadeIn">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Content */}
              <div>
                <div className="inline-block mb-6 px-6 py-3 bg-red-600 text-white rounded-full text-sm font-bold animate-pulse shadow-lg shadow-red-700/50">
                  ⚡ GARAGE EPOXY SPECIALISTS - 35% OFF
                </div>
                
                <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                  Transform Your
                  <span className="block text-red-600 mt-2">Garage Today</span>
                </h1>
                
                <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
                  Sydney's most trusted garage epoxy specialists. Durable, stunning floors that last a lifetime.
                </p>

                <div className="grid grid-cols-3 gap-6 mb-8">
                  <div className="text-center p-4 bg-red-600/10 rounded-xl border border-red-700/20">
                    <div className="text-4xl font-bold text-red-600 mb-2">15+</div>
                    <div className="text-gray-400 text-sm">Years Experience</div>
                  </div>
                  <div className="text-center p-4 bg-red-600/10 rounded-xl border border-red-700/20">
                    <div className="text-4xl font-bold text-red-600 mb-2">500+</div>
                    <div className="text-gray-400 text-sm">Garages Done</div>
                  </div>
                  <div className="text-center p-4 bg-red-600/10 rounded-xl border border-red-700/20">
                    <div className="text-4xl font-bold text-red-600 mb-2">98%</div>
                    <div className="text-gray-400 text-sm">Satisfaction</div>
                  </div>
                </div>

                <div className="flex items-center space-x-4 text-gray-300">
                  <CheckCircle2 className="w-6 h-6 text-red-600" />
                  <span>Free Quotes</span>
                  <CheckCircle2 className="w-6 h-6 text-red-600" />
                  <span>5-Year Workmanship Warranty</span>
                  <CheckCircle2 className="w-6 h-6 text-red-600" />
                  <span>Fast Service</span>
                </div>
              </div>

              {/* Right Side - Form */}
              <Card className="border-2 border-red-600/50 shadow-2xl shadow-red-700/20 bg-white backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="text-center mb-8">
                    <div className="inline-block px-4 py-2 bg-red-600 text-white rounded-full text-sm font-bold mb-4">
                      CLAIM YOUR 35% DISCOUNT
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Garage Epoxy Flooring Quote</h2>
                    <p className="text-gray-600">Fill out the form and we'll contact you within 24 hours</p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">Full Name *</label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        required
                        className="h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-red-600"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">Phone Number *</label>
                      <Input
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="04XX XXX XXX"
                        required
                        className="h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-red-600"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">Email Address *</label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        required
                        className="h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-red-600"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">Garage Details</label>
                      <Textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        placeholder="Tell us about your garage... (size, current condition, timeline, etc.)"
                        rows={5}
                        className="resize-none bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-red-600"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full bg-red-600 hover:bg-red-700 text-white font-bold h-14 text-lg transition-all duration-200 hover:scale-105 shadow-lg shadow-red-700/30"
                    >
                      <Send className="w-5 h-5 mr-2" />
                      Get My Garage Quote Now
                    </Button>

                    <p className="text-xs text-gray-600 text-center">
                      By submitting this form, you agree to receive communication from Alpha Epoxy Flooring Sydney
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Why Choose Alpha Epoxy */}
        <section className="py-24" style={{ backgroundColor: '#1C2541' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Why Choose Alpha Epoxy for Your Garage?</h2>
              <p className="text-xl text-gray-300">Professional quality that lasts</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { icon: <Shield className="w-8 h-8 text-white" />, title: "Durable & Long-Lasting", desc: "Industrial-grade epoxy that withstands heavy traffic and wear" },
                { icon: <Sparkles className="w-8 h-8 text-white" />, title: "Beautiful Finish", desc: "Transform your garage into a stunning, showroom-quality space" },
                { icon: <Clock className="w-8 h-8 text-white" />, title: "Fast Installation", desc: "Most garage floors completed in 2-3 days with minimal disruption" },
                { icon: <Award className="w-8 h-8 text-white" />, title: "5-Year Workmanship Warranty", desc: "Comprehensive warranty coverage on all garage installations" }
              ].map((feature, index) => (
                <Card key={index} className="border-2 border-red-600/30 shadow-lg hover:shadow-red-700/30 transition-all duration-300 hover:-translate-y-2 bg-white/5 backdrop-blur-sm">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                    <p className="text-gray-300 leading-relaxed">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Comprehensive Content Section */}
        <section className="py-24 bg-gray-800">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Professional Garage Epoxy Flooring Solutions in Sydney
              </h2>
              <div className="w-24 h-1 bg-red-600 mx-auto mb-8"></div>
            </div>

            <div className="space-y-8 text-gray-300 text-lg leading-relaxed">
              <p>
                Looking to transform your garage in Sydney? <span className="text-white font-semibold">Alpha Epoxy Flooring</span> specializes in premium garage epoxy flooring that combines exceptional durability with stunning aesthetics. Our garage floor coatings are designed to withstand the harsh Australian climate while maintaining their beautiful finish for years to come.
              </p>

              <p>
                <span className="text-red-600 font-semibold">Why choose epoxy for your garage?</span> Garage epoxy flooring creates a seamless, non-porous surface that protects your concrete from oil stains, chemical spills, and daily wear and tear. Unlike traditional garage floor paint, epoxy creates a chemical bond with the concrete, resulting in a surface that's up to 10 times more durable than untreated concrete.
              </p>

              <div className="bg-black/30 border-l-4 border-red-600 p-6 rounded-r-lg">
                <h3 className="text-2xl font-bold text-white mb-4">Our Garage Epoxy Services Include:</h3>
                <ul className="grid md:grid-cols-2 gap-4">
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span><strong className="text-white">Solid Color Epoxy:</strong> Classic, professional finish in your choice of colors</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span><strong className="text-white">Flake Epoxy Systems:</strong> Decorative chips for added texture and style</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span><strong className="text-white">Metallic Epoxy:</strong> Stunning 3D effects for luxury garage aesthetics</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span><strong className="text-white">Clear Coat Finishes:</strong> Ultimate protection with high-gloss sheen</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span><strong className="text-white">Anti-Slip Additives:</strong> Enhanced safety for wet conditions</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span><strong className="text-white">Custom Designs:</strong> Logos, patterns, and custom color combinations</span>
                  </li>
                </ul>
              </div>

              <p>
                Our process begins with professional diamond grinding to ensure perfect adhesion. We repair any cracks or imperfections, apply a moisture barrier if needed, and then install your chosen epoxy system. The result? A garage floor that's not only incredibly durable but also easy to clean and maintain.
              </p>

              <div className="bg-red-600/10 border border-red-600/30 p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-white mb-4">Benefits of Garage Epoxy Flooring:</h3>
                <div className="grid md:grid-cols-2 gap-4 text-base">
                  <div className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-red-600 mr-2 mt-1" />
                    <span><strong>Oil & Chemical Resistant:</strong> Protects against automotive fluids and household chemicals</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-red-600 mr-2 mt-1" />
                    <span><strong>Easy to Clean:</strong> Simply hose down or mop for spotless results</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-red-600 mr-2 mt-1" />
                    <span><strong>Increases Property Value:</strong> Professional garage floors add appeal and value</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-red-600 mr-2 mt-1" />
                    <span><strong>UV Stable:</strong> Won't yellow or fade in Sydney's harsh sunlight</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-red-600 mr-2 mt-1" />
                    <span><strong>Dust-Free Surface:</strong> Seals concrete to eliminate dust and debris</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-red-600 mr-2 mt-1" />
                    <span><strong>Impact Resistant:</strong> Handles dropped tools and heavy equipment</span>
                  </div>
                </div>
              </div>

              <p className="text-center text-xl">
                Serving all Sydney areas, we bring <span className="text-red-600">15+ years of expertise</span> and a commitment to excellence to every garage floor project. From single-car garages to multi-car workshops, we deliver professional results that exceed expectations.
              </p>
            </div>

            <div className="text-center mt-12">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white font-bold px-12 py-6 text-xl shadow-lg shadow-red-600/30">
                <PhoneCall className="w-6 h-6 mr-3" />
                Get Your Garage Epoxy Flooring Quote Now
              </Button>
              <p className="text-gray-400 mt-4 text-sm">Free consultation • Professional assessment • No obligation quote</p>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">What Our Garage Customers Say</h2>
              <p className="text-xl text-gray-600">Real reviews from Sydney homeowners</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {mockData.testimonials.slice(0, 3).map((testimonial, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="flex items-center mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-red-600 text-red-600" />
                      ))}
                    </div>
                    <p className="text-gray-700 mb-6 leading-relaxed italic">"{testimonial.review}"</p>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-red-600/30">
                        {testimonial.name.charAt(0)}
                      </div>
                      <div className="ml-4">
                        <p className="font-semibold text-gray-900">{testimonial.name}</p>
                        <p className="text-sm text-gray-600">{testimonial.location}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Garage Epoxy FAQ</h2>
              <p className="text-xl text-gray-600">Common questions about garage floor epoxy</p>
            </div>

            <div className="space-y-4">
              {[
                {
                  question: "How long does garage epoxy installation take?",
                  answer: "Most garage floors are completed in 2-3 days. Day 1 involves surface preparation and repairs, Day 2 is the epoxy application, and Day 3 (if needed) is for top coat and finishing touches. You can typically use your garage within 24-48 hours after completion."
                },
                {
                  question: "Can I drive on epoxy garage floors?",
                  answer: "Yes! Our garage epoxy systems are specifically designed for vehicle traffic. The epoxy creates a surface that's stronger and more durable than bare concrete. We recommend waiting 24 hours for light foot traffic and 72 hours before driving vehicles on the new surface."
                },
                {
                  question: "How do I clean and maintain my epoxy garage floor?",
                  answer: "Epoxy garage floors are incredibly easy to maintain. Simply sweep regularly and mop with mild soap and water when needed. For tougher stains, use a non-abrasive cleaner. The seamless surface means no dirt or grime can get trapped, making cleaning a breeze."
                },
                {
                  question: "Will epoxy work in my garage?",
                  answer: "Epoxy works excellently in most garages. We assess your concrete's condition, moisture levels, and any existing coatings during our free consultation. If your garage has specific challenges, we'll recommend the best solution or alternative coating system."
                },
                {
                  question: "What colors and finishes are available?",
                  answer: "We offer a wide range of solid colors, custom color combinations, flake systems with various chip sizes and colors, and stunning metallic finishes. During your consultation, we'll show you samples and help you choose the perfect look for your garage."
                },
                {
                  question: "Is garage epoxy slippery when wet?",
                  answer: "We can add anti-slip additives to your top coat for enhanced traction, especially useful in wet conditions. Our standard finishes provide good grip, but we recommend anti-slip options for garages in high-rainfall areas or where water accumulation is common."
                }
              ].map((faq, index) => (
                <Card key={index} className="border-none shadow-md hover:shadow-lg transition-all duration-200">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Map Section - Serving Sydney */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Serving Sydney Without Limits</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From North Shore to Southern Sydney, Eastern Suburbs to Western Sydney - 
                we bring premium garage epoxy solutions to your doorstep
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Map */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-red-600 h-[500px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d424143.94532033163!2d150.65179054788284!3d-33.84714479524214!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b129838f39a743f%3A0x3017d681632a850!2sSydney%20NSW%2C%20Australia!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Sydney Service Area Map"
                ></iframe>
              </div>

              {/* Service Areas */}
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">Coverage Across Greater Sydney</h3>
                <p className="text-gray-600 text-lg mb-8">
                  No matter where you are in Sydney, our expert team is ready to transform your garage 
                  with premium epoxy flooring solutions. We service all suburbs and areas.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  {[
                    "Northern Beaches",
                    "North Shore",
                    "Inner West",
                    "Eastern Suburbs",
                    "Western Sydney",
                    "Southern Sydney",
                    "Hills District",
                    "Sutherland Shire",
                    "Parramatta Area",
                    "Canterbury-Bankstown",
                    "Blacktown",
                    "Penrith"
                  ].map((area, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircle2 className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
                      <span className="text-gray-700 font-medium">{area}</span>
                    </div>
                  ))}
                </div>

                <Card className="border-2 border-red-600/50 bg-red-50">
                  <CardContent className="p-6">
                    <h4 className="text-xl font-bold text-gray-900 mb-3">Free On-Site Assessment</h4>
                    <p className="text-gray-700 mb-4">
                      We'll visit your location anywhere in Sydney for a free, no-obligation consultation 
                      and provide an accurate quote on the spot.
                    </p>
                    <Button className="bg-red-600 hover:bg-red-700 text-white font-bold">
                      <PhoneCall className="w-4 h-4 mr-2" />
                      Book Your Free Visit
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default GarageEpoxy;